// How often visible data refreshes on its own.
//
// There is no refresh button anywhere: data an owner watches keeps itself
// current while the app is on screen, and everything refetches when the app
// returns to the foreground or the network comes back (the query client's
// focus and reconnect defaults). Polling pauses while the app is hidden.
//
// While the server's event stream is connected it pushes new readings and
// announces device reports, configuration and refused samples, so polling is
// only a safety net. Without it (connecting, or blocked by a network), live
// data polls. Intervals follow how fast each kind of data changes and how
// expensive it is, not how fast a device could report.

import type { QueryKey } from '@tanstack/react-query';
import { isStreamLive } from './liveStream';

/** Readings, status, devices and configuration: what an owner watches. */
export const LIVE_INTERVAL_MS = 20_000;
/** The same, while the event stream keeps them current. */
export const STREAMED_INTERVAL_MS = 5 * 60_000;
/** Groups and administrative lists: change only when someone edits them. */
export const SLOW_INTERVAL_MS = 60_000;
/** The module catalog: changes when an administrator publishes. */
export const CATALOG_INTERVAL_MS = 5 * 60_000;

/** The polling interval for a query, or `false` for none. */
export function refreshInterval(queryKey: QueryKey): number | false {
  // Only account data polls; the session and sign-in providers refresh on
  // focus and reconnect, which is enough.
  if (queryKey[0] !== 'account') return false;
  const parts = queryKey.slice(2);
  if (parts.includes('admin')) return SLOW_INTERVAL_MS;
  if (parts[0] === 'modules') return CATALOG_INTERVAL_MS;
  if (parts[0] === 'groups' || parts[0] === 'group') return SLOW_INTERVAL_MS;
  // An older page of history does not change; only the newest page polls.
  if (parts.includes('readings') && parts[parts.length - 1] !== 'first') return false;
  // A chart range is expensive to refetch (it pages over its whole span), so it
  // does not poll. New readings reach it through the event stream; without the
  // stream a slow poll keeps the newest point from going stale.
  if (parts.includes('chart')) return isStreamLive() ? false : SLOW_INTERVAL_MS;
  return isStreamLive() ? STREAMED_INTERVAL_MS : LIVE_INTERVAL_MS;
}
